import "./resultSeriesStyle.scss";
import { useMantineColorScheme } from "@mantine/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import moment from "moment";
import FullscreenModal from "../common/FullscreenModal/FullscreenModal";
import { useTranslation } from "react-i18next";
import { formatEnUsValue, useCurrency } from "../../utils/helpers";

// Secure unique ID generator: uses window.crypto when available,
// falls back to timestamp + counter to avoid collisions without Math.random.
let __uidCounter = 0;
function secureUniqueId(prefix = "uid") {
  try {
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
      // 128-bit random, encoded as hex to avoid BigInt for eslint compatibility
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${prefix}-${hex}`;
    }
  } catch (e) {
    // ignore and use fallback
  }
  const ts = Date.now().toString(36);
  __uidCounter = (__uidCounter + 1) % 1_000_000;
  return `${prefix}-${ts}-${__uidCounter.toString(36)}`;
}

// Dynamic series configuration: each entry lists possible source keys (lowercased) and an assigned color.
// Add/adjust mappings as new result series become available.
const SERIES_CONFIG = [
  { id: "load", labels: ["load", "loads"], color: "#4992ff", label: "Load" },
  {
    id: "battery",
    labels: ["battery", "batterypower"],
    color: "#7cffb2",
    label: "BESS",
  },
  {
    id: "pv",
    labels: [
      "pv",
      "solargeneration",
      "solar",
      "solar_generation",
      "solar-generation",
    ],
    color: "#58d9f9",
    label: "PV",
  },
  {
    id: "genset",
    labels: ["diesel", "dieselgeneration", "genset", "generator"],
    color: "#05c091",
    label: "Genset",
  },
  {
    id: "grid",
    labels: ["grid", "gridpower", "grid_total", "gridtotal", "grid-power"],
    color: "#ff9f40",
    label: "Grid",
  },
];

const SOC_CONFIG = {
  id: "battery_soc",
  labels: [
    "batterysoc",
    "battery_soc",
    "battery-soc",
    "stateofcharge",
    "state_of_charge",
    "soc",
  ],
  color: "#f66d9b",
  label: "Battery SoC",
};

const fontFamily = "Nunito, sans-serif";

const ResultSeries = ({
  results,
  onFullscreen,
  kpiProjectSettings,
  inModalInstance = false,
  legendGap = 16,
}) => {
  const { t } = useTranslation();
  const currency = useCurrency();

  const { colorScheme } = useMantineColorScheme();
  const wrapperRef = useRef(null);
  const mainRef = useRef(null);
  // Minimap removed for now (overlap issue). Keeping refs/state minimal.
  const tooltipRef = useRef(null);
  const [hidden, setHidden] = useState(new Set());
  const [xDomain, setXDomain] = useState(null); // active zoom domain
  const [yMode, setYMode] = useState("full"); // 'full' or 'window'
  const [panMode, setPanMode] = useState(false); // NEW pan toggle
  const [powerUnit, setPowerUnit] = useState("W"); // 'W' | 'kW'
  const [fullscreenModal, setFullscreenModal] = useState(false);

  const unitHelpers = React.useMemo(() => {
    const unitFactor = powerUnit === "kW" ? 1 / 1000 : 1;
    const unitLabel = powerUnit;
    const scaleValue = (v) => {
      const num = +v;
      return Number.isFinite(num) ? num * unitFactor : null;
    };
    const formatValue = (v) => {
      if (v == null || !Number.isFinite(v)) return "-";
      const hasDecimal = Math.abs(v % 1) > 1e-6;
      return hasDecimal ? (Math.round(v * 1000) / 1000).toString() : v.toString();
    };
    const powerAxisLabel =
      powerUnit === "kW"
        ? t("timeSeries.axis.powerKW")
        : t("timeSeries.axis.powerW");
    return { unitFactor, unitLabel, scaleValue, formatValue, powerAxisLabel };
  }, [powerUnit, t]);

  const handleCloseModal = useCallback(() => {
    setFullscreenModal(false);
    if (typeof onFullscreen === "function") onFullscreen();
  }, [onFullscreen]);

  // Build dynamic series list from available result keys.
  const series = React.useMemo(() => {
    const r = results || {};
    const lowerMap = Object.keys(r).reduce((acc, k) => {
      acc[k.toLowerCase()] = k;
      return acc;
    }, {});
    const picked = [];
    SERIES_CONFIG.forEach((cfg) => {
      const match = cfg.labels.find((l) => lowerMap[l]);
      if (match) {
        const origKey = lowerMap[match];
        const raw = r[origKey];
        if (Array.isArray(raw) && raw.length) {
          picked.push({
            key: cfg.id,
            sourceKey: origKey,
            label: cfg.label,
            color: cfg.color,
            values: raw,
          });
        }
      }
    });
    return picked;
  }, [results]);

  const socSeries = React.useMemo(() => {
    const r = results || {};
    const lowerMap = Object.keys(r).reduce((acc, k) => {
      acc[k.toLowerCase()] = k;
      return acc;
    }, {});

    const match = SOC_CONFIG.labels.find((l) => lowerMap[l]);
    if (!match) return null;

    const origKey = lowerMap[match];
    const raw = r[origKey];
    if (!Array.isArray(raw) || !raw.length) return null;

    return {
      key: SOC_CONFIG.id,
      sourceKey: origKey,
      label: SOC_CONFIG.label,
      color: SOC_CONFIG.color,
      values: raw,
    };
  }, [results]);

  // Determine unified timeline length (max length among series). Assume 15‑minute spacing for 1 year back start anchor.
  const maxLen = useMemo(() => {
    const lengths = [...series.map((s) => s.values.length)];
    if (socSeries?.values?.length) lengths.push(socSeries.values.length);
    return lengths.length ? Math.max(...lengths) : 0;
  }, [series, socSeries]);
  const stepMs = 15 * 60 * 1000;
  const startDate = useRef(null);
  if (maxLen && !startDate.current) {
    if (kpiProjectSettings?.startYear) {
      // Use startYear from kpiProjectSettings (e.g., 2024)
      // Create date in LOCAL timezone at 00:00:00 (not UTC)
      const d = new Date(kpiProjectSettings.startYear, 0, 1, 0, 0, 0, 0);
      startDate.current = d;
    } else {
      // Fallback: use one year back from current date
      const d = new Date();
      d.setFullYear(d.getFullYear() - 1);
      // Ensure it's set to 00:00:00 in local timezone
      d.setHours(0, 0, 0, 0);
      startDate.current = d;
    }
  }
  const times = React.useMemo(() => {
    if (!maxLen || !startDate.current) return [];
    const base = startDate.current.getTime();
    const arr = new Array(maxLen);
    for (let i = 0; i < maxLen; i++) arr[i] = new Date(base + i * stepMs);
    return arr;
  }, [maxLen]);

  const sampleIndices = (startIdx, endIdx, maxPoints) => {
    const span = endIdx - startIdx + 1;
    if (span <= maxPoints) {
      return d3.range(startIdx, endIdx + 1);
    }
    const step = Math.ceil(span / maxPoints);
    const arr = [];
    for (let i = startIdx; i <= endIdx; i += step) arr.push(i);
    if (arr[arr.length - 1] !== endIdx) arr.push(endIdx);
    return arr;
  };

  const render = () => {
    const root = wrapperRef.current;
    if (!root) return;
    const { unitLabel, scaleValue, formatValue, powerAxisLabel } = unitHelpers;
    const mainSvg = d3.select(mainRef.current);
    mainSvg.selectAll("*").remove();
    // Legend is now built later (after computing window stats) for min/max display
    if (!maxLen) {
      mainSvg
        .append("text")
        .attr("x", 20)
        .attr("y", 30)
        .text(t("common.noDataAvailable"))
        .attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057");
      return;
    }

    const chartScale = 1.18;
    const width = root.clientWidth;
    const hasSocData = !!(socSeries && socSeries.values && socSeries.values.length);
    const hasSocAxis = hasSocData && !hidden.has(SOC_CONFIG.id);
    const rightPadding = hasSocAxis ? 72 * chartScale : 20 * chartScale;
    const fullExtent = d3.extent(times);
    const fullX = d3
      .scaleTime()
      .domain(fullExtent)
      .range([60, width - rightPadding]);
    // Clamp domain to available data range, then extend by half a step at each end
    let domainStart = xDomain ? xDomain[0] : times[0];
    let domainEnd = xDomain ? xDomain[1] : times[maxLen - 1];
    if (domainStart < times[0]) domainStart = times[0];
    if (domainEnd > times[maxLen - 1]) domainEnd = times[maxLen - 1];
    // Store original domain bounds for tooltip visibility check
    const originalDomainStart = domainStart;
    const originalDomainEnd = domainEnd;
    // Extend domain by half a step before and after for rendering
    const step = times.length > 1 ? times[1] - times[0] : stepMs;
    domainStart = new Date(domainStart.getTime() - step / 2);
    domainEnd = new Date(domainEnd.getTime() + step / 2);
    const currentDomain = [domainStart, domainEnd];

    // Main chart
    const mainH = 470 * chartScale;
    // const mainH = Math.max(320, Math.round(wrapperRef.current?.clientWidth * 0.5));

    // Use dynamic legendGap for top margin
    const margin = {
      left: 60 * chartScale,
      right: rightPadding,
      top: legendGap * chartScale,
      bottom: 60 * chartScale,
    };
    mainSvg
      .attr("width", width)
      .attr("height", 550 * chartScale)
      .style("background", colorScheme === "dark" ? "#111111" : "#ffffff");
    // Use direct domain for zoom/pan, no manual padding
    const viewX = fullX.copy().domain(currentDomain);

    // Determine indices in window first (needed for dynamic y max)
    let startIdx = d3.bisector((d) => d).left(times, currentDomain[0]);
    let endIdx = d3.bisector((d) => d).right(times, currentDomain[1]) - 1;
    // Always include one extra point before and after for edge visibility
    startIdx = Math.max(0, startIdx - 1);
    endIdx = Math.min(times.length - 1, endIdx + 1);

    // Active series (not hidden)
    const activeSeries = series.filter((s) => !hidden.has(s.key));

    // Compute global full-range min/max across ALL time for active series (for 'full' y-mode)
    let globalMax = Number.NEGATIVE_INFINITY;
    let globalMin = Number.POSITIVE_INFINITY;
    for (const s of activeSeries) {
      const arr = s.values || [];
      for (let i = 0; i < arr.length; i++) {
        const num = scaleValue(arr[i]);
        if (!Number.isFinite(num)) continue;
        if (num > globalMax) globalMax = num;
        if (num < globalMin) globalMin = num;
      }
    }
    if (globalMax === Number.NEGATIVE_INFINITY) {
      globalMax = 1;
      globalMin = 0;
    }
    if (globalMin === Number.POSITIVE_INFINITY) globalMin = 0;

    // Compute min & max across visible series within CURRENT DOMAIN (for 'window' mode), coercing to numbers
    let maxValWindow = Number.NEGATIVE_INFINITY;
    let minValWindow = Number.POSITIVE_INFINITY;
    for (const s of activeSeries) {
      const vals = s.values || [];
      for (let i = startIdx; i <= endIdx && i < vals.length; i++) {
        const num = scaleValue(vals[i]);
        if (!Number.isFinite(num)) continue;
        if (num > maxValWindow) maxValWindow = num;
        if (num < minValWindow) minValWindow = num;
      }
    }
    if (maxValWindow === Number.NEGATIVE_INFINITY) {
      // fallback to any numeric in full range (maybe window empty)
      for (const s of activeSeries) {
        const nums = (s.values || [])
          .map((v) => +v)
          .filter((v) => Number.isFinite(v));
        if (!nums.length) continue;
        const localMax = d3.max(nums);
        const localMin = d3.min(nums);
        if (localMax != null && localMax > maxValWindow)
          maxValWindow = localMax;
        if (localMin != null && localMin < minValWindow)
          minValWindow = localMin;
      }
    }
    if (maxValWindow === Number.NEGATIVE_INFINITY) maxValWindow = 1; // no data safeguard
    if (minValWindow === Number.POSITIVE_INFINITY) minValWindow = 0;

    // Decide which range to use based on yMode
    let useMax = yMode === "full" ? globalMax : maxValWindow;
    let useMin = yMode === "full" ? globalMin : minValWindow;
    // If all positive, baseline at zero
    if (useMin > 0) useMin = 0;
    if (useMax === useMin) {
      if (useMax === 0) {
        useMax = 1;
      } else {
        useMin = 0;
      }
    }
    // Tight y-domain: remove previous 5% padding and use exact extrema (with minimal epsilon for flat lines)
    let yBottomBase = useMin; // already forced to 0 if all positive
    let yTop = useMax;
    if (yTop === yBottomBase) {
      // flat line safeguard
      if (yTop === 0) {
        yTop = 1;
      } else {
        yBottomBase = 0;
      }
    }
    // Minimal epsilon to avoid clipping due to pixel rounding (0.2% of span)
    const spanY = yTop - yBottomBase;
    const eps = spanY * 0.002;
    yTop += eps;
    const viewY = d3.scaleLinear().domain([yBottomBase, yTop]);
    const niceDomain = viewY.domain();
    viewY.range([mainH - margin.bottom, margin.top]);
    // (startIdx/endIdx already computed)
    const idxs = sampleIndices(startIdx, endIdx, 4000); // sampling cap

    // Restored simpler x-axis logic with adaptive 15-min resolution when zoomed.
    const spanMs = currentDomain[1] - currentDomain[0];
    const dayMs = 24 * 60 * 60 * 1000;
    const plotW = width - 80; // approximate drawable width
    const targetLabelWidth = 80; // px per label budget
    const maxTicks = Math.max(
      4,
      Math.min(14, Math.floor(plotW / targetLabelWidth))
    );
    let xAxis = d3.axisBottom(viewX);
    // --- Minimap (overview) with brush, from index.js ---
    const minimapHeight = 48;
    const minimapGap = 8;
    const minimapMargin = { left: 60, right: margin.right };
    const minimapWidth = width;
    const minimapY = mainH + minimapGap;
    // Minimap SVG group
    const minimapSvg = mainSvg
      .append("g")
      .attr("class", "minimap")
      .attr("transform", `translate(0,${minimapY})`);
    // Minimap background
    minimapSvg
      .append("rect")
      .attr("x", minimapMargin.left)
      .attr("y", 0)
      .attr("width", minimapWidth - minimapMargin.left - minimapMargin.right)
      .attr("height", minimapHeight)
      .attr("fill", colorScheme === "dark" ? "#1a1a1a" : "#f5f5f5");
    // Minimap x/y scales
    const miniX = d3
      .scaleTime()
      .domain([times[0], times[times.length - 1]])
      .range([minimapMargin.left, minimapWidth - minimapMargin.right]);
    // Compute global min/max for minimap y-axis
    let miniYMin = Number.POSITIVE_INFINITY;
    let miniYMax = Number.NEGATIVE_INFINITY;
    series.forEach((s) => {
      (s.values || []).forEach((v) => {
        const num = +v;
        if (!Number.isFinite(num)) return;
        if (num < miniYMin) miniYMin = num;
        if (num > miniYMax) miniYMax = num;
      });
    });
    if (!Number.isFinite(miniYMin)) miniYMin = 0;
    if (!Number.isFinite(miniYMax)) miniYMax = 1;
    if (miniYMin > 0) miniYMin = 0;
    if (miniYMax === miniYMin) miniYMax = miniYMin + 1;
    const miniY = d3
      .scaleLinear()
      .domain([miniYMin, miniYMax])
      .range([minimapHeight - 8, 8]);
    // Minimap chart group (above background, below brush/selection)
    const minimapChartG = minimapSvg
      .append("g")
      .attr("class", "minimap-chart-lines");
    if (activeSeries.length && times.length) {
      activeSeries.forEach((s) => {
        // Build [x, y] pairs for valid points only
        const points = [];
        for (let i = 0; i < s.values.length && i < times.length; i++) {
          const v = +s.values[i];
          if (!Number.isFinite(v)) continue;
          points.push([miniX(times[i]), miniY(v)]);
        }
        if (points.length > 1) {
          const lineGen = d3
            .line()
            .x((d) => d[0])
            .y((d) => d[1]);
          minimapChartG
            .append("path")
            .attr("fill", "none")
            .attr("stroke", s.color)
            .attr("stroke-width", 1.2)
            .attr("opacity", 0.95)
            .attr("d", lineGen(points));
        }
      });
    } else {
      // Fallback: draw a placeholder flat line in minimap if no data
      const fallbackY = miniY(0.5 * (miniYMin + miniYMax));
      minimapChartG
        .append("path")
        .attr("fill", "none")
        .attr("stroke", "#bbb")
        .attr("stroke-width", 1)
        .attr("opacity", 0.7)
        .attr(
          "d",
          d3.line()(
            [minimapMargin.left, minimapWidth - minimapMargin.right].map(
              (x) => [x, fallbackY]
            )
          )
        );
    }
    // --- Minimap crosshair drag selection ---
    const crosshairOverlay = minimapSvg
      .append("rect")
      .attr("class", "minimap-crosshair-overlay")
      .attr("x", minimapMargin.left)
      .attr("y", 0)
      .attr("width", minimapWidth - minimapMargin.left - minimapMargin.right)
      .attr("height", minimapHeight)
      .attr("fill", "transparent");
    // .style("cursor", "crosshair");

    let dragStartX = null;
    let dragEndX = null;
    let dragRect = null;

    // crosshairOverlay.on("mousedown", function(ev) {
    //   const rect = this.getBoundingClientRect();
    //   dragStartX = Math.max(minimapMargin.left, Math.min(ev.clientX - rect.left + minimapMargin.left, minimapWidth - minimapMargin.right));
    //   dragEndX = dragStartX;
    //   dragRect = minimapSvg.append("rect")
    //     .attr("class", "minimap-crosshair-selection")
    //     .attr("x", dragStartX)
    //     .attr("y", 0)
    //     .attr("width", 1)
    //     .attr("height", minimapHeight)
    //     .attr("fill", colorScheme === "dark" ? "rgba(80,160,255,0.18)" : "rgba(0,123,255,0.13)")
    //     .attr("stroke", colorScheme === "dark" ? "#4da3ff" : "#007bff")
    //     .attr("stroke-width", 1.2)
    //     .attr("pointer-events", "none");
    //   window.addEventListener("mousemove", onDragMove);
    //   window.addEventListener("mouseup", onDragUp);
    // });

    const onDragMove = (ev) => {
      const rect = crosshairOverlay.node().getBoundingClientRect();
      dragEndX = Math.max(
        minimapMargin.left,
        Math.min(
          ev.clientX - rect.left + minimapMargin.left,
          minimapWidth - minimapMargin.right
        )
      );
      const x0 = Math.min(dragStartX, dragEndX);
      const x1 = Math.max(dragStartX, dragEndX);
      dragRect.attr("x", x0).attr("width", Math.max(1, x1 - x0));
    };

    const onDragUp = (ev) => {
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("mouseup", onDragUp);
      if (dragRect) dragRect.remove();
      if (
        dragStartX !== null &&
        dragEndX !== null &&
        Math.abs(dragEndX - dragStartX) > 5
      ) {
        const x0 = Math.min(dragStartX, dragEndX);
        const x1 = Math.max(dragStartX, dragEndX);
        const t0 = miniX.invert(x0);
        const t1 = miniX.invert(x1);
        // Clamp to bounds and require at least 3 points
        const idx0 = d3.bisector((d) => d).left(times, t0);
        const idx1 = d3.bisector((d) => d).right(times, t1) - 1;
        if (idx1 - idx0 + 1 >= 3 && t1 - t0 > stepMs * 2) {
          setXDomain([t0, t1]);
        }
      }
      dragStartX = null;
      dragEndX = null;
      dragRect = null;
    };
    // Minimap brush logic
    let ignoreBrushEvent = false;
    const brush = d3
      .brushX()
      .extent([
        [minimapMargin.left, 0],
        [minimapWidth - minimapMargin.right, minimapHeight],
      ])
      .on("brush end", function (event) {
        if (ignoreBrushEvent) return;
        if (!event.selection) return;
        const [x0, x1] = event.selection;
        const d0 = miniX.invert(x0);
        const d1 = miniX.invert(x1);
        if (event.type === "brush" || event.type === "end") {
          setXDomain([d0, d1]);
        }
      });
    const brushG = minimapSvg
      .append("g")
      .attr("class", "minimap-brush")
      .call(brush);
    // Set pointer-events: none only on the overlay rect inside the brush group
    brushG.selectAll("rect.overlay").style("pointer-events", "none");
    // Move brush to current domain
    // Prevent infinite loop: only move brush if selection is different
    const desired = xDomain
      ? [miniX(xDomain[0]), miniX(xDomain[1])]
      : [miniX(times[0]), miniX(times[times.length - 1])];
    const current = d3.brushSelection(brushG.node());
    if (!current || current[0] !== desired[0] || current[1] !== desired[1]) {
      ignoreBrushEvent = true;
      brushG.call(brush.move, desired);
      ignoreBrushEvent = false;
    }
    // Add selection rectangle to minimap and make it draggable for panning
    if (xDomain) {
      const selX0 = miniX(xDomain[0]);
      const selX1 = miniX(xDomain[1]);
      const selectionRect = minimapSvg
        .append("rect")
        .attr("class", "minimap-selection")
        .attr("x", selX0)
        .attr("y", 0)
        .attr("width", Math.max(1, selX1 - selX0))
        .attr("height", minimapHeight)
        .attr(
          "fill",
          colorScheme === "dark"
            ? "rgba(80,160,255,0.18)"
            : "rgba(0,123,255,0.13)"
        )
        .attr("stroke", colorScheme === "dark" ? "#4da3ff" : "#007bff")
        .attr("stroke-width", 1.2)
        .style("cursor", "grab");

      let dragStartX = null;
      let dragDomain = null;
      selectionRect.on("pointerdown", function (ev) {
        ev.preventDefault();
        if (ev.button !== 0) return;
        let dragging = true;
        const startX = ev.clientX;
        const startDomain = [...xDomain];
        const selX0 = miniX(xDomain[0]);
        const selX1 = miniX(xDomain[1]);
        const domainSpanMs = startDomain[1] - startDomain[0];
        const msPerPixel = domainSpanMs / Math.max(1, selX1 - selX0);

        const onPointerMove = (e) => {
          if (!dragging) return;
          const dx = e.clientX - startX;
          let shiftMs = dx * msPerPixel;
          let newStart = new Date(startDomain[0].getTime() + shiftMs);
          let newEnd = new Date(startDomain[1].getTime() + shiftMs);
          // Clamp to bounds
          if (newStart < times[0]) {
            newStart = times[0];
            newEnd = new Date(newStart.getTime() + domainSpanMs);
          }
          if (newEnd > times[times.length - 1]) {
            newEnd = times[times.length - 1];
            newStart = new Date(newEnd.getTime() - domainSpanMs);
          }
          setXDomain([newStart, newEnd]);
        };

        const onPointerUp = () => {
          dragging = false;
          window.removeEventListener("pointermove", onPointerMove, true);
          window.removeEventListener("pointerup", onPointerUp, true);
          selectionRect.style("cursor", "grab");
        };

        selectionRect.style("cursor", "grabbing");
        window.addEventListener("pointermove", onPointerMove, {
          passive: true,
          capture: true,
        });
        window.addEventListener("pointerup", onPointerUp, {
          passive: true,
          capture: true,
        });
      });
    }

    // Decide granularity
    if (spanMs <= 16 * dayMs) {
      // Use 15-min base intervals, pick stride so ticks <= maxTicks
      const intervalsInView = spanMs / stepMs; // 15-min intervals
      let stride = Math.ceil(intervalsInView / maxTicks); // number of 15-min slots per tick
      // Snap stride to friendly multiples (1,2,4,8,12,16,24,32,48,64)
      const friendly = [1, 2, 4, 8, 12, 16, 24, 32, 48, 64, 96];
      stride = friendly.find((f) => f >= stride) || stride;
      const minutes = stride * 15;
      if (minutes < 24 * 60) {
        xAxis = xAxis.ticks(d3.timeMinute.every(minutes));
      } else {
        // large stride -> daily ticks
        xAxis = xAxis.ticks(d3.timeDay.every(1));
      }
      xAxis.tickFormat(d3.timeFormat("%-d %b %Y %-H:%M"));
    } else if (spanMs <= 120 * dayMs) {
      // Up to ~4 months -> weekly ticks
      xAxis = xAxis
        .ticks(d3.timeWeek.every(1))
        .tickFormat(d3.timeFormat("%-d %b %Y"));
    } else {
      // Larger span -> monthly ticks
      xAxis = xAxis
        .ticks(d3.timeMonth.every(1))
        .tickFormat(d3.timeFormat("%b %Y"));
    }
    const maxForFormat = Math.max(
      Math.abs(niceDomain[0]),
      Math.abs(niceDomain[1])
    );
    const fmtThousands = d3.format(",");
    const fmtShort = d3.format(".2s");
    const yAxis = d3
      .axisLeft(viewY)
      .ticks(5)
      .tickFormat((d) => {
        const abs = Math.abs(d);
        if (maxForFormat >= 1e9) return fmtShort(d).replace("G", "B");
        if (maxForFormat >= 1e6) return fmtShort(d); // shows e.g. 1.2M
        // Under 1M show plain thousands with separators
        return fmtThousands(d);
      });

    const hasSoc = !!(socSeries && socSeries.values && socSeries.values.length);
    const showSoc = hasSoc && !hidden.has(SOC_CONFIG.id);
    const socAllFinite = hasSoc
      ? (socSeries.values || [])
          .map((v) => +v)
          .filter((v) => Number.isFinite(v))
      : [];
    const socNeedsPercentScale =
      socAllFinite.length > 0 &&
      d3.max(socAllFinite) <= 1 &&
      d3.min(socAllFinite) >= 0;
    const toSocPercent = (v) => {
      const num = +v;
      if (!Number.isFinite(num)) return null;
      return socNeedsPercentScale ? num * 100 : num;
    };
    const socValsInWindow = hasSoc
      ? idxs
          .filter((j) => j < socSeries.values.length)
          .map((j) => toSocPercent(socSeries.values[j]))
          .filter((v) => Number.isFinite(v))
      : [];
    const socMinRaw = socValsInWindow.length ? d3.min(socValsInWindow) : 0;
    const socMaxRaw = socValsInWindow.length ? d3.max(socValsInWindow) : 100;
    const socMin = Math.max(0, Math.min(100, socMinRaw ?? 0));
    const socMax = Math.max(0, Math.min(100, socMaxRaw ?? 100));
    const socBottom = Math.min(0, socMin);
    const socTop = socMax === socBottom ? socBottom + 1 : socMax;
    const socY = d3
      .scaleLinear()
      .domain([socBottom, socTop])
      .range([mainH - margin.bottom, margin.top]);
    // Build / refresh legend UI with min & max values in current window
    const legendContainer = root.querySelector(".rs-legend");
    if (legendContainer) {
      legendContainer.innerHTML = "";
      const rawValue = (value) =>
        value == null || !Number.isFinite(value) ? "-" : formatEnUsValue(value);
      series.forEach((s) => {
        let wMin = Infinity;
        let wMax = -Infinity;
        for (let i = startIdx; i <= endIdx && i < s.values.length; i++) {
          const num = scaleValue(s.values[i]);
          if (!Number.isFinite(num)) continue;
          if (num < wMin) wMin = num;
          if (num > wMax) wMax = num;
        }
        if (wMin === Infinity) {
          wMin = null;
          wMax = null;
        }
        const btn = document.createElement("button");
        btn.type = "button";
        btn.style.display = "flex";
        btn.style.alignItems = "center";
        btn.style.gap = "6px";
        btn.style.margin = "2px 10px";
        btn.style.padding = "4px 10px";
        btn.style.border = "1px solid #ccc";
        btn.style.borderRadius = "4px";
        btn.style.cursor = "pointer";
        btn.style.background = hidden.has(s.key)
          ? colorScheme === "dark"
            ? "#1e1e1e"
            : "#f1f3f5"
          : colorScheme === "dark"
          ? "#2e2e2e"
          : "#ffffff";
        btn.style.opacity = hidden.has(s.key) ? "0.45" : "1";
        btn.title = hidden.has(s.key) ? `Show ${s.label}` : `Hide ${s.label}`;
        const swatch = document.createElement("span");
        swatch.style.width = "14px";
        swatch.style.height = "8px";
        swatch.style.borderRadius = "2px";
        swatch.style.background = s.color;
        swatch.style.flexShrink = "0";
        const line = document.createElement("span");
        line.style.fontSize = "11px";
        line.style.fontFamily = fontFamily;
        line.style.color = colorScheme === "dark" ? "#d5d7e0" : "#222";
        line.textContent = `${s.label}: Min ${rawValue(wMin)} ${unitLabel} , Max ${rawValue(wMax)} ${unitLabel}`;
        btn.appendChild(swatch);
        btn.appendChild(line);
        btn.addEventListener("click", () => {
          setHidden((prev) => {
            const next = new Set(prev);
            if (next.has(s.key)) next.delete(s.key);
            else next.add(s.key);
            return next;
          });
        });
        legendContainer.appendChild(btn);
      });

      if (hasSoc) {
        const socBtn = document.createElement("button");
        socBtn.type = "button";
        socBtn.style.display = "flex";
        socBtn.style.alignItems = "center";
        socBtn.style.gap = "6px";
        socBtn.style.margin = "2px 10px";
        socBtn.style.padding = "4px 10px";
        socBtn.style.border = "1px solid #ccc";
        socBtn.style.borderRadius = "4px";
        socBtn.style.cursor = "pointer";
        socBtn.style.background = hidden.has(SOC_CONFIG.id)
          ? colorScheme === "dark"
            ? "#1e1e1e"
            : "#f1f3f5"
          : colorScheme === "dark"
          ? "#2e2e2e"
          : "#ffffff";
        socBtn.style.opacity = hidden.has(SOC_CONFIG.id) ? "0.45" : "1";
        socBtn.title = hidden.has(SOC_CONFIG.id)
          ? `Show ${socSeries.label}`
          : `Hide ${socSeries.label}`;

        const swatch = document.createElement("span");
        swatch.style.width = "14px";
        swatch.style.height = "8px";
        swatch.style.borderRadius = "2px";
        swatch.style.background = socSeries.color;
        swatch.style.flexShrink = "0";

        const line = document.createElement("span");
        line.style.fontSize = "11px";
        line.style.fontFamily = fontFamily;
        line.style.color = colorScheme === "dark" ? "#d5d7e0" : "#222";
        line.textContent = `${socSeries.label}: Min ${rawValue(socMinRaw)} % , Max ${rawValue(socMaxRaw)} %`;

        socBtn.appendChild(swatch);
        socBtn.appendChild(line);
        socBtn.addEventListener("click", () => {
          setHidden((prev) => {
            const next = new Set(prev);
            if (next.has(SOC_CONFIG.id)) next.delete(SOC_CONFIG.id);
            else next.add(SOC_CONFIG.id);
            return next;
          });
        });
        legendContainer.appendChild(socBtn);
      }
    }
    const axisOffset = 16; // push axis labels below zero line
    const xAxisG = mainSvg
      .append("g")
      .attr("transform", `translate(0,${viewY.range()[0] + axisOffset})`)
      .call(xAxis);
    xAxisG
      .selectAll("text")
      .attr("font-size", 11)
      .attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057")
      .attr("font-family", fontFamily)
      .attr("text-anchor", "middle");
    // Light overlap handling: ensure first/last always visible, hide every other if crowded
    const tickTexts = xAxisG.selectAll("text").nodes();
    if (tickTexts.length > 14) {
      const skip = Math.ceil(tickTexts.length / 14);
      tickTexts.forEach((n, i) => {
        if (i !== 0 && i !== tickTexts.length - 1 && i % skip !== 0)
          n.style.display = "none";
      });
    }
    mainSvg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(yAxis)
      .selectAll("text")
      .attr("font-size", 11)
      .attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057")
      .attr("font-family", fontFamily);
    mainSvg.selectAll(".domain,.tick line").attr("stroke", "none");
    // Zero reference line if negative values present
    if (niceDomain[0] < 0) {
      mainSvg
        .append("line")
        .attr("x1", margin.left)
        .attr("x2", width - margin.right)
        .attr("y1", viewY(0))
        .attr("y2", viewY(0))
        .attr("stroke", colorScheme === "dark" ? "#555" : "#999")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "4,4");
    }
    mainSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -(mainH / 2))
      .attr("y", 18)
      .attr("text-anchor", "middle")
      .attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057")
      .attr("font-size", 12)
      .text(powerAxisLabel);

    if (showSoc) {
      const yAxisRight = d3.axisRight(socY).ticks(5).tickFormat((d) => `${Math.round(d)}%`);
      mainSvg
        .append("g")
        .attr("transform", `translate(${width - margin.right},0)`)
        .call(yAxisRight)
        .selectAll("text")
        .attr("font-size", 11)
        .attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057")
        .attr("font-family", fontFamily);

      mainSvg
        .append("text")
        .attr("transform", "rotate(90)")
        .attr("x", mainH / 2)
        .attr("y", -(width - margin.right + 40))
        .attr("text-anchor", "middle")
        .attr("fill", colorScheme === "dark" ? "#d5d7e0" : "#495057")
        .attr("font-size", 12)
        .text("Battery SoC (%)");
    }

    // Add D3 clipping path for main chart area (crypto-safe ID)
    const clipId = secureUniqueId("clip");
    mainSvg
      .append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("x", margin.left)
      .attr("y", margin.top)
      .attr("width", width - margin.left - margin.right)
      .attr("height", viewY.range()[0] - margin.top);
    const dataLayer = mainSvg
      .append("g")
      .attr("class", "data-layer")
      .attr("clip-path", `url(#${clipId})`);
    series.forEach((s) => {
      if (hidden.has(s.key)) return;
      const l = d3
        .line()
        .defined(
          (j) => {
            if (j >= s.values.length || s.values[j] == null) return false;
            const scaled = scaleValue(s.values[j]);
            return scaled != null && Number.isFinite(scaled);
          }
        )
        .x((j) => viewX(times[j]))
        .y((j) => viewY(scaleValue(s.values[j])));
      const visibleIdxs = idxs.filter((j) => j < times.length);
      const pathData = l(visibleIdxs);
      const dataLayer = mainSvg
        .append("g")
        .attr("class", "data-layer")
        .attr("clip-path", `url(#${clipId})`);
      if (pathData) {
        dataLayer
          .append("path")
          .attr("d", pathData)
          .attr("fill", "none")
          .attr("stroke", s.color)
          .attr("stroke-width", 1.4)
          .attr("opacity", 0.95);
        // Do not draw all dots; dots will be rendered only on hover
      }
    });

    if (showSoc) {
      const socLine = d3
        .line()
        .defined((j) => j < socSeries.values.length && Number.isFinite(toSocPercent(socSeries.values[j])))
        .x((j) => viewX(times[j]))
        .y((j) => socY(toSocPercent(socSeries.values[j])));

      const visibleIdxs = idxs.filter((j) => j < times.length);
      const socPath = socLine(visibleIdxs);
      if (socPath) {
        const socLayer = mainSvg
          .append("g")
          .attr("class", "data-layer")
          .attr("clip-path", `url(#${clipId})`);

        socLayer
          .append("path")
          .attr("d", socPath)
          .attr("fill", "none")
          .attr("stroke", socSeries.color)
          .attr("stroke-width", 1.4)
          .attr("opacity", 0.95);
      }
    }

    // Tooltip
    if (!tooltipRef.current) {
      const t = document.createElement("div");
      t.style.position = "absolute";
      t.style.pointerEvents = "none";
      t.style.background = colorScheme === "dark" ? "#222" : "#fff";
      t.style.border =
        "1px solid " + (colorScheme === "dark" ? "#555" : "#666");
      t.style.padding = "6px 8px";
      t.style.fontSize = "12px";
      t.style.fontFamily = fontFamily;
      t.style.borderRadius = "4px";
      t.style.boxShadow = "0 2px 6px rgba(0,0,0,.25)";
      t.style.display = "none";
      t.style.zIndex = 40;
      tooltipRef.current = t;
      root.appendChild(t);
      root.style.position = "relative";
    }
    const tooltip = tooltipRef.current;
    const plotWidth = width - margin.left - margin.right;
    const hoverRect = mainSvg
      .append("rect")
      .attr("x", margin.left)
      .attr("y", margin.top)
      .attr("width", plotWidth)
      .attr("height", viewY.range()[0] - margin.top)
      .attr("fill", "transparent")
      .style(
        "cursor",
        panMode ? (xDomain ? "grab" : "not-allowed") : "crosshair"
      );

    let selection = null;
    let selectionRect = null;
    let panRef = null;

    hoverRect.on("mousedown", (ev) => {
      ev.preventDefault();
      const pt = d3.pointer(ev);
      if (ev.button !== 0) return;
      if (panMode) {
        let domainForPan = xDomain;
        if (!domainForPan) {
          // Auto create a narrower window (50% span) so user can pan immediately
          const fullSpan = fullExtent[1] - fullExtent[0];
          const newSpan = fullSpan * 0.5;
          const mid = new Date(
            (fullExtent[0].getTime() + fullExtent[1].getTime()) / 2
          );
          const start = new Date(mid.getTime() - newSpan / 2);
          const end = new Date(mid.getTime() + newSpan / 2);
          setXDomain([start, end]);
          domainForPan = [start, end];
        }
        panRef = { startX: pt[0], domain: [...domainForPan] };
        hoverRect.style("cursor", "grabbing");
        const panMoveHandler = (e) => {
          const ptMove = d3.pointer(e, mainRef.current);
          let dx = ptMove[0] - panRef.startX; // pixels
          const spanMs = panRef.domain[1] - panRef.domain[0];
          if (spanMs <= 0) return;
          const msPerPixel = spanMs / plotWidth;
          let shiftMs = -dx * msPerPixel;
          // clamp shiftMs to bounds
          const minShiftMs = fullExtent[0] - panRef.domain[0]; // cannot go earlier than full start
          const maxShiftMs = fullExtent[1] - panRef.domain[1]; // cannot go beyond full end
          if (shiftMs < minShiftMs) shiftMs = minShiftMs;
          if (shiftMs > maxShiftMs) shiftMs = maxShiftMs;
          const newStart = new Date(panRef.domain[0].getTime() + shiftMs);
          const newEnd = new Date(panRef.domain[1].getTime() + shiftMs);
          setXDomain([newStart, newEnd]);
        };
        window.addEventListener("mousemove", panMoveHandler);
        window.addEventListener("mouseup", function panUpHandler() {
          window.removeEventListener("mousemove", panMoveHandler);
          window.removeEventListener("mouseup", panUpHandler);
          hoverRect.style(
            "cursor",
            panMode ? (xDomain ? "grab" : "not-allowed") : "crosshair"
          );
        });
      } else {
        selection = { startX: pt[0], endX: pt[0] };
        selectionRect = mainSvg
          .append("rect")
          .attr("class", "zoom-box")
          .attr("x", pt[0])
          .attr("y", margin.top)
          .attr("width", 0)
          .attr("height", viewY.range()[0] - margin.top)
          .attr(
            "fill",
            colorScheme === "dark"
              ? "rgba(80,160,255,0.20)"
              : "rgba(0,123,255,0.15)"
          )
          .attr("stroke", colorScheme === "dark" ? "#4da3ff" : "#007bff")
          .attr("stroke-dasharray", "3,2");
        hoverRect.style("cursor", "col-resize");
        window.addEventListener("mouseup", onPointerUp, { once: true });
      }
    });

    const onSelectionMove = (ev) => {
      if (!selection) return;
      const pt = d3.pointer(ev);
      selection.endX = pt[0];
      const x0 = Math.max(
        margin.left,
        Math.min(selection.startX, selection.endX)
      );
      const x1 = Math.min(
        margin.left + plotWidth,
        Math.max(selection.startX, selection.endX)
      );
      selectionRect.attr("x", x0).attr("width", Math.max(0, x1 - x0));
    };
    const onPanMove = (ev) => {
      if (!panRef) return;
      const pt = d3.pointer(ev);
      let dx = pt[0] - panRef.startX; // pixels
      const spanMs = panRef.domain[1] - panRef.domain[0];
      if (spanMs <= 0) return;
      const msPerPixel = spanMs / plotWidth;
      let shiftMs = -dx * msPerPixel;
      // clamp shiftMs to bounds
      const minShiftMs = fullExtent[0] - panRef.domain[0]; // cannot go earlier than full start
      const maxShiftMs = fullExtent[1] - panRef.domain[1]; // cannot go beyond full end
      if (shiftMs < minShiftMs) shiftMs = minShiftMs;
      if (shiftMs > maxShiftMs) shiftMs = maxShiftMs;
      // Compute new domain and update immediately
      const newStart = new Date(panRef.domain[0].getTime() + shiftMs);
      const newEnd = new Date(panRef.domain[1].getTime() + shiftMs);
      setXDomain([newStart, newEnd]);
    };
    const onPointerUp = () => {
      if (selection) {
        const x0 = Math.max(
          margin.left,
          Math.min(selection.startX, selection.endX)
        );
        const x1 = Math.min(
          margin.left + plotWidth,
          Math.max(selection.startX, selection.endX)
        );
        if (x1 - x0 > 5) {
          const d0 = viewX.invert(x0);
          const d1 = viewX.invert(x1);
          // Restrict zoom if fewer than 3 points would be visible
          const minPoints = 3;
          const idx0 = d3.bisector((d) => d).left(times, d0);
          const idx1 = d3.bisector((d) => d).right(times, d1) - 1;
          if (idx1 - idx0 + 1 >= minPoints && d1 - d0 > stepMs * 2) {
            setXDomain([d0, d1]);
          }
        }
        selectionRect && selectionRect.remove();
      }
      selection = null;
      selectionRect = null;
      hoverRect.style(
        "cursor",
        panMode ? (xDomain ? "grab" : "not-allowed") : "crosshair"
      );
    };
    const onPointerUpPan = () => {
      if (panRef) {
        const shiftMs = panRef.shiftMs || 0;
        if (shiftMs) {
          const newStart = new Date(panRef.domain[0].getTime() + shiftMs);
          const newEnd = new Date(panRef.domain[1].getTime() + shiftMs);
          setXDomain([newStart, newEnd]);
        }
      }
      dataLayer.attr("transform", null);
      panRef = null;
      hoverRect.style(
        "cursor",
        panMode ? (xDomain ? "grab" : "not-allowed") : "crosshair"
      );
    };

    const vLine = mainSvg
      .append("line")
      .attr("stroke", colorScheme === "dark" ? "#888" : "#666")
      .attr("stroke-dasharray", "3,3")
      .style("display", "none");
    const onTooltipMove = (ev) => {
      if (selection || panRef) return;
      const [mx] = d3.pointer(ev);
      const date = viewX.invert(mx);
      if (!date) return;
      const idx = d3.bisector((d) => d).center(times, date);
      if (idx < 0 || idx >= times.length) return;
      // Use original domain bounds (before extension) for tooltip visibility check
      if (times[idx] < originalDomainStart || times[idx] > originalDomainEnd) {
        tooltip.style.display = "none";
        vLine.style("display", "none");
        // Reset all dots
        series.forEach((s) => {
          if (hidden.has(s.key)) return;
          d3.selectAll(`.dot-${s.key}`).attr("r", 2).attr("fill", s.color);
        });
        return;
      }
      vLine
        .attr("x1", viewX(times[idx]))
        .attr("x2", viewX(times[idx]))
        .attr("y1", margin.top)
        .attr("y2", viewY.range()[0])
        .style("display", "block");
      let html = `<strong>${times[idx].toLocaleString()}</strong><br/>`;
      series.forEach((s) => {
        if (hidden.has(s.key)) return;
        const v = scaleValue(s.values[idx]);
        if (v == null || isNaN(v)) return;
        html += `<span style=\"color:${s.color}\">●</span> ${s.label}: <b>${formatEnUsValue(v)}</b> ${unitLabel}<br/>`;
      });
      const socAtIdx = showSoc && idx < socSeries.values.length ? toSocPercent(socSeries.values[idx]) : null;
      if (Number.isFinite(socAtIdx)) {
        html += `<span style=\"color:${socSeries.color}\">●</span> ${socSeries.label}: <b>${formatEnUsValue(socAtIdx)}</b> %<br/>`;
      }
      tooltip.innerHTML = html;
      tooltip.style.left = viewX(times[idx]) + 12 + "px";
      tooltip.style.top = margin.top + 8 + "px";
      tooltip.style.display = "block";
      // Remove all previous dots
      d3.selectAll(".dot").remove();
      // Draw only the hovered dot for each series
      const dataLayer = d3.select(mainRef.current).select(".data-layer");
      series.forEach((s) => {
        if (hidden.has(s.key)) return;
        const v = scaleValue(s.values[idx]);
        if (v == null || isNaN(v)) return;
        dataLayer
          .append("circle")
          .attr("class", `dot dot-${s.key}`)
          .attr("cx", viewX(times[idx]))
          .attr("cy", viewY(v))
          .attr("r", 2)
          .attr("fill", "#eb7857")
          .attr("stroke", colorScheme === "dark" ? "#fff" : "#222")
          .attr("stroke-width", 1.2)
          .attr("opacity", 0.95);
      });
      if (Number.isFinite(socAtIdx)) {
        dataLayer
          .append("circle")
          .attr("class", `dot dot-${socSeries.key}`)
          .attr("cx", viewX(times[idx]))
          .attr("cy", socY(socAtIdx))
          .attr("r", 2)
          .attr("fill", "#eb7857")
          .attr("stroke", colorScheme === "dark" ? "#fff" : "#222")
          .attr("stroke-width", 1.2)
          .attr("opacity", 0.95);
      }
    };
    const onTooltipLeave = () => {
      if (selection || panRef) return;
      tooltip.style.display = "none";
      vLine.style("display", "none");
      // Reset all dots to default style on mouse leave
      series.forEach((s) => {
        if (hidden.has(s.key)) return;
        d3.selectAll(`.dot-${s.key}`).attr("r", 2).attr("fill", s.color);
      });
    };

    // Attach namespaced handlers so they don't overwrite each other
    hoverRect
      .on("mousemove.zoom", panMode ? null : onSelectionMove)
      .on("mousemove.pan", panMode ? onPanMove : null)
      .on("mousemove.tooltip", onTooltipMove)
      .on("mouseleave", onTooltipLeave);
  }

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;
    const mainSvg = d3.select(mainRef.current);
    const minimapSvg = mainSvg.select(".minimap");
    if (!minimapSvg.empty() && xDomain) {
      const hasSocAxis = !!(socSeries?.values?.length) && !hidden.has(SOC_CONFIG.id);
      const rightPadding = hasSocAxis ? 72 * 1.18 : 20 * 1.18;
      const lengths = [...series.map((s) => s.values.length)];
      if (socSeries?.values?.length) lengths.push(socSeries.values.length);
      const maxLocalLen = lengths.length ? Math.max(...lengths) : 0;
      const times = maxLocalLen
        ? Array.from({ length: maxLocalLen }, (_, i) => {
            const d = new Date();
            d.setFullYear(d.getFullYear() - 1);
            return new Date(d.getTime() + i * 15 * 60 * 1000);
          })
        : [];
      if (!times.length) return;
      const miniX = d3
        .scaleTime()
        .domain([times[0], times[times.length - 1]])
        .range([60, root.clientWidth - rightPadding]);
      minimapSvg
        .select(".minimap-brush")
        .call(d3.brushX().move, [miniX(xDomain[0]), miniX(xDomain[1])]);
    }
  }, [xDomain, series, socSeries, hidden]);
  useEffect(() => {
    render();
  }, [series, socSeries, colorScheme, hidden, times, xDomain, panMode, powerUnit, unitHelpers]);
  useEffect(() => {
    const onR = () => render();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, [results, colorScheme, hidden, unitHelpers]);

  return (
    <div
      ref={wrapperRef}
      className={`rs-wrapper ${colorScheme === "dark" ? "dark" : "light"}`}
    >
      <div className="rs-toolbar">
        <button
          className="rs-btn"
          aria-label={inModalInstance ? "time-series-close-fullscreen" : "time-series-open-fullscreen"}
          title={inModalInstance ? t("common.closeFullscreen") : t("common.openFullscreen")}
          onClick={() => {
            if (inModalInstance) {
              handleCloseModal();
            } else {
              setFullscreenModal(true);
            }
          }}
        >
          <span role="img" aria-hidden="true" className="rs-pan-icon">
            {inModalInstance ? "🗗" : "⛶"}
          </span>
        </button>
        <button
          className={`rs-btn rs-btn-pan ${panMode ? "is-active" : ""}`}
          aria-label={panMode ? "time-series-disable-pan" : "time-series-enable-pan"}
          title={
            panMode
              ? "Pan enabled (click to disable)"
              : "Pan disabled (click to enable)"
          }
          aria-pressed={panMode}
          onClick={() => setPanMode((p) => !p)}
        >
          <span role="img" aria-hidden="true" className="rs-pan-icon">
            {panMode ? "🖐️" : "↔"}
          </span>
        </button>
        <button
          className="rs-btn"
          aria-label="time-series-zoom-in"
          title={t("common.zoomIn")}
          onClick={() => {
            if (!times.length) return;
            const full = [times[0], times[times.length - 1]];
            const minPoints = 3;
            if (!xDomain) {
              const span = full[1] - full[0];
              const newSpan = span * 0.5;
              const mid = new Date((full[0].getTime() + full[1].getTime()) / 2);
              const d0 = new Date(mid.getTime() - newSpan / 2);
              const d1 = new Date(mid.getTime() + newSpan / 2);
              const idx0 = d3.bisector((d) => d).left(times, d0);
              const idx1 = d3.bisector((d) => d).right(times, d1) - 1;
              if (idx1 - idx0 + 1 >= minPoints) {
                setXDomain([d0, d1]);
              }
              return;
            }
            const old = xDomain;
            const span = old[1] - old[0];
            const mid = new Date((old[0].getTime() + old[1].getTime()) / 2);
            let newSpan = span * 0.5;
            const minSpan = stepMs * 4;
            if (newSpan < minSpan) newSpan = minSpan;
            const d0 = new Date(mid.getTime() - newSpan / 2);
            const d1 = new Date(mid.getTime() + newSpan / 2);
            const idx0 = d3.bisector((d) => d).left(times, d0);
            const idx1 = d3.bisector((d) => d).right(times, d1) - 1;
            if (idx1 - idx0 + 1 >= minPoints) {
              setXDomain([d0, d1]);
            }
          }}
        >
          ＋
        </button>
        <button
          className="rs-btn"
          aria-label="time-series-zoom-out"
          title={t("common.zoomOut")}
          onClick={() => {
            if (!times.length) return;
            const full = [times[0], times[times.length - 1]];
            if (!xDomain) return;
            const old = xDomain;
            const span = old[1] - old[0];
            const mid = new Date((old[0].getTime() + old[1].getTime()) / 2);
            let newSpan = span / 0.7;
            const fullSpan = full[1] - full[0];
            if (newSpan >= fullSpan) {
              setXDomain(null);
              return;
            }
            setXDomain([
              new Date(mid.getTime() - newSpan / 2),
              new Date(mid.getTime() + newSpan / 2),
            ]);
          }}
        >
          －
        </button>
        <button
          className="rs-btn"
          aria-label="time-series-reset-zoom"
          title={t("common.resetZoom")}
          onClick={() => setXDomain(null)}
        >
          Reset
        </button>
        <select
          className="rs-btn rs-download-select"
          value={powerUnit}
          title={t("timeSeries.unitToggle")}
          onChange={(e) => setPowerUnit(e.target.value)}
          style={{ width: 90 }}
        >
          <option value="W">W</option>
          <option value="kW">kW</option>
        </select>
        <select
          className="rs-btn rs-download-select"
          title={t("common.download")}
          value=""
          onChange={(e) => {
            const value = e.target.value;
            if (value === "png") {
              // Download PNG
              const svgEl = mainRef.current;
              if (!svgEl) return;
              const serializer = new XMLSerializer();
              const cloned = svgEl.cloneNode(true);
              const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
              bg.setAttribute("x", "0");
              bg.setAttribute("y", "0");
              bg.setAttribute("width", svgEl.clientWidth || svgEl.getAttribute("width") || 1200);
              bg.setAttribute("height", svgEl.clientHeight || svgEl.getAttribute("height") || 600);
              bg.setAttribute(
                "fill",
                colorScheme === "dark" ? "#111" : "#ffffff"
              );
              cloned.insertBefore(bg, cloned.firstChild);
              const svgString = serializer.serializeToString(cloned);
              const img = new Image();
              const canvas = document.createElement("canvas");
              const w = svgEl.clientWidth || parseInt(svgEl.getAttribute("width"),10) || 1200;
              const h = svgEl.clientHeight || parseInt(svgEl.getAttribute("height"),10) || 600;
              canvas.width = w;
              canvas.height = h;
              const ctx = canvas.getContext("2d");
              img.onload = () => {
                ctx.drawImage(img, 0, 0);
                canvas.toBlob((blob) => {
                  if (!blob) return;
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  const ts = new Date().toISOString().replace(/[:.]/g, "-");
                  a.download = `result-series-${ts}.png`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                });
              };
              img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);
            } else if (value === "csv") {
              // Download CSV with DateTime and all series columns
              try {
                if (!times.length || !series.length) return;
                const { unitLabel, scaleValue } = unitHelpers;

                const getResultsArrayInsensitive = (obj, key) => {
                  if (!obj || !key) return null;
                  if (Array.isArray(obj[key])) return obj[key];
                  const lower = String(key).toLowerCase();
                  const matchKey = Object.keys(obj).find((k) => String(k).toLowerCase() === lower);
                  return matchKey && Array.isArray(obj[matchKey]) ? obj[matchKey] : null;
                };

                // Extra CSV-only columns (do not show on chart)
                const csvOnlyCols = [
                  {
                    key: "purchaseCost",
                    header: `Import Tariff (${currency || "$"}/kWh)`,
                    values: getResultsArrayInsensitive(results, "purchaseCost"),
                  },
                  {
                    key: "sellbackCost",
                    header: `Export Tariff (${currency || "$"}/kWh)`,
                    values: getResultsArrayInsensitive(results, "sellbackCost"),
                  },
                  {
                    key: "penalty",
                    header: `Penalty (${currency || "$"}/kWh)`,
                    values: getResultsArrayInsensitive(results, "penalty"),
                  },
                  {
                    key: "gridImport",
                    header: `Grid Import (${unitLabel})`,
                    values: getResultsArrayInsensitive(results, "gridImport"),
                  },
                  {
                    key: "gridExport",
                    header: `Grid Export (${unitLabel})`,
                    values: getResultsArrayInsensitive(results, "gridExport"),
                  },
                  {
                    key: "gridExcess",
                    header: `Grid Excess (${unitLabel})`,
                    values: getResultsArrayInsensitive(results, "gridExcess"),
                  },
                  {
                    key: "gridUnmet",
                    header: `Grid Unmet (${unitLabel})`,
                    values: getResultsArrayInsensitive(results, "gridUnmet"),
                  },
                  {
                    key: "batteryCharge",
                    header: `Battery Charge (${unitLabel})`,
                    values: getResultsArrayInsensitive(results, "batteryCharge"),
                  },
                  {
                    key: "batteryDischarge",
                    header: `Battery Discharge (${unitLabel})`,
                    values: getResultsArrayInsensitive(results, "batteryDischarge"),
                  },
                  {
                    key: "batterySOC",
                    header: "Battery SOC (0-1)",
                    values: getResultsArrayInsensitive(results, "batterySOC"),
                  },
                  {
                    key: "tariffEffective",
                    header: `Tariff Effective (${currency || "$"}/kWh)`,
                    values: getResultsArrayInsensitive(results, "tariffEffective"),
                  },
                  {
                    key: "tariffSubscribed",
                    header: `Tariff Subscribed (${currency || "$"}/kWh)`,
                    values: getResultsArrayInsensitive(results, "tariffSubscribed"),
                  },
                ].filter((c) => Array.isArray(c.values) && c.values.length);

                // CSV-escape: wrap in quotes if value contains comma, quote, or non-ASCII
                const csvEscape = (val) => {
                  const s = String(val);
                  if (/[,"\n\r]/.test(s) || /[^\x20-\x7E]/.test(s)) {
                    return '"' + s.replace(/"/g, '""') + '"';
                  }
                  return s;
                };

                const rows = [];
                const headerCols = [
                  "DateTime",
                  ...series.map((s) => `${s.label} (${unitLabel})`),
                  ...csvOnlyCols.map((c) => c.header),
                ];
                rows.push(headerCols.map(csvEscape).join(","));

                for (let i = 0; i < times.length; i++) {
                  const dateStr = moment(times[i]).format("YYYY-MM-DD HH:mm:ss");
                  const vals = series.map((s) => {
                    const v = scaleValue(s.values[i]);
                    return v !== undefined && v !== null ? v : "";
                  });
                  const csvOnlyVals = csvOnlyCols.map((c) => {
                    const v = c.values?.[i];
                    return v !== undefined && v !== null ? v : "";
                  });
                  rows.push([dateStr, ...vals, ...csvOnlyVals].join(","));
                }
                const csvContent = rows.join("\n");
                // Prepend UTF-8 BOM so Excel correctly renders Unicode chars (€, £, ¥, etc.)
                const BOM = "\uFEFF";
                const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                const ts = new Date().toISOString().replace(/[:.]/g, "-");
                a.download = `result-series-${ts}.csv`;
                a.href = url;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                // Silent fail
              }
            }
            // Reset dropdown
            e.target.selectedIndex = 0;
          }}
        >
          <option value="" disabled hidden>{t("common.download") || "Download"}</option>
          <option value="png">PNG</option>
          <option value="csv">CSV</option>
        </select>
      </div>
      <div className="rs-legend" />
      <svg ref={mainRef} className="indicator-downloadable rs-chart" />
      <FullscreenModal
        open={fullscreenModal && !inModalInstance}
        onClose={handleCloseModal}
        title={t("result.timeSeries.title")}
      >
        <ResultSeries
          results={results}
          onFullscreen={handleCloseModal}
          inModalInstance={true}
          kpiProjectSettings={kpiProjectSettings}
        />
      </FullscreenModal>
    </div>
  );
};

export default ResultSeries;
